import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { motion, AnimatePresence } from "framer-motion";

import CrossIcon from "../../../../lib/components/Icons/CrossIcon";
import { GIG_DETAIL_INTENT_PAYMENT } from "../../../gig/common/constants";
import LayoutModalBody from "./LayoutModalBody";
import LayoutModalFooter from "./LayoutModalFooter";

const LayoutModal = ({
  id,
  children,
  showModal,
  setShowModal,
  showCloseIcon,
  shouldCloseOnBackgroundClick = true,
  className,
  changeStates = [],
  modalHeight = "h-176",
  minModalHeight = "min-h-screen-50",
  maxModalHeight = "max-h-90vh",
  closeButtonClassnames = "top-0 mt-4 right-4",
  borderRadiusClassnames = "rounded-t-2xl md:rounded-md",
  onCloseModal = () => {},
  modalWidth = "md:w-136 w-full",
  ...props
}) => {
  const location = useLocation();
  const modalContentRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle resize to update mobile state
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Disable background overflow and reset it in cleanup.
  useEffect(() => {
    if (showModal) {
      document.documentElement.classList.add("hide-scrollbar");
    } else {
      document.documentElement.classList.remove("hide-scrollbar");
    }

    return () => {
      document.documentElement.classList.remove("hide-scrollbar");
      if (!showModal) {
        onCloseModal();
      }
    };
  }, [showModal, onCloseModal]);

  const closeModal = (e) => {
    if (!shouldCloseOnBackgroundClick) return undefined;

    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(e.target)
    ) {
      if (location.pathname.includes(GIG_DETAIL_INTENT_PAYMENT)) {
        window.location.reload();
      }
      handleClose();
    }
  };

  const handleClose = () => {
    if (setShowModal) {
      for (const stateChangeFunction of changeStates) {
        stateChangeFunction(false);
      }
      setShowModal(false);
    }
  };

  // Modal animation variants with different animations for mobile and desktop
  const modalVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? "100%" : 0,
      scale: isMobile ? 1 : 0.96,
      transition: {
        type: "tween",
        duration: 0.25,
        ease: "easeOut",
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 400,
        mass: 0.8,
      },
    },
    exit: {
      opacity: 0,
      y: isMobile ? "50%" : 0,
      scale: isMobile ? 1 : 0.95,
      transition: {
        type: "tween",
        duration: 0.2,
        ease: [0.4, 0, 1, 1], // Custom ease-in curve
      },
    },
  };

  return (
    <AnimatePresence mode="wait">
      {showModal && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center md:items-center"
          initial="hidden"
          animate="visible"
          exit="exit"
          onMouseDown={(e) => {
            e.persist();
            closeModal(e);
          }}
          {...props}
        >
          {/* Background Overlay */}
          <motion.div
            className="fixed top-0 z-20 h-screen w-screen bg-secondary-dark-700 bg-opacity-60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            style={{ transform: "translateZ(0)" }}
          />
          {/* End of Background Overlay */}
          {/* Modal */}
          <motion.div
            ref={modalContentRef}
            className={`show-scrollbar-on-hover fixed z-20 ${modalWidth} ${maxModalHeight} ${minModalHeight} bg-white dark:bg-secondary-dark-600 md:${modalHeight} overflow-y-auto md:min-h-0 md:shadow-modal ${
              className || ""
            } ${borderRadiusClassnames}`}
            id={id ? id : "layout-modal"}
            variants={modalVariants}
            style={{
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
              transform: "translateZ(0)",
            }}
          >
            {children}
            {showCloseIcon && (
              <motion.button
                className={`outline-none focus:outline-none absolute rounded-full text-gray-500 ${closeButtonClassnames}`}
                type="button"
                onClick={handleClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <CrossIcon className="h-4 w-4 cursor-pointer text-space hover:text-space-80 dark:text-white dark:hover:text-space-40" />
              </motion.button>
            )}
          </motion.div>
          {/* End of Modal */}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Object.assign(LayoutModal, {
  Footer: LayoutModalFooter,
  Body: LayoutModalBody,
});
